import React, { useState, useEffect } from 'react';
import "./AprovarCotacao.css";
import { Button, Table, message } from 'antd';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import ReenviarSolicitacao from './ReenviarSolicitacao';
import ViewSolicitacaoModal from '../Solicitacoes/ViewSolicitacaoModalADM';
import SelecionarFornecedor from '../Fornecedores/SelecionarFornecedor';
import api from '../../../services/api';
import API_CONFIG from '../../../config/api.config';

const AprovarCotacao = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { idCotacao } = useParams();
  const isMobile = useMediaQuery({ maxWidth: 600 });

  // Estado para cotação real
  const [cotacao, setCotacao] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aprovando, setAprovando] = useState(false);

  // Buscar cotação real ao montar
  useEffect(() => {
    const fetchCotacao = async () => {
      setLoading(true);
      try {
        const response = await api.get(API_CONFIG.ENDPOINTS.COTACOES.LISTAR_ESPECIFICA(idCotacao));
        if (response.data.status) {
          setCotacao(response.data.cotacao);
        }
      } catch (e) {
        setCotacao(null);
      } finally {
        setLoading(false);
      }
    };
    fetchCotacao();
  }, [idCotacao]);

  // Fornecedores mockados para visualização (serão substituídos pelos itens reais)
  // O array de fornecedores será montado a partir dos itens da cotação real
  const fornecedoresAPI = cotacao?.item_cotacao?.map((item, idx) => ({
    nome: item.item?.IT_NOME || `Item ${idx+1}`,
    status: item.item?.IT_STATUS || '-',
    valor: Number(item.IC_VALOR) || 0,
    quantidade: Number(item.item?.IT_QUANTIDADE) || 0,
    valorTotal: (Number(item.IC_VALOR) || 0) * (Number(item.item?.IT_QUANTIDADE) || 0),
    selecionado: true, // Mantém selecionado por padrão, igual ao mock
  })) || [];

  // Estado de seleção dos fornecedores (itens)
  const [fornecedores, setFornecedores] = useState([]);
  useEffect(() => {
    if (fornecedoresAPI.length > 0) {
      setFornecedores(fornecedoresAPI);
    }
  }, [cotacao]);

  const [modalReenviarVisivel, setModalReenviarVisivel] = useState(false);
  const [modalFornecedoresVisivel, setModalFornecedoresVisivel] = useState(false);
  const [fornecedoresReenvio, setFornecedoresReenvio] = useState([]);
  const [itensFaltantes, setItensFaltantes] = useState([]);

  // Permitir seleção múltipla
  const handleSelecionarFornecedor = (idx) => {
    setFornecedores(fornecedores.map((f, i) =>
      i === idx ? { ...f, selecionado: !f.selecionado } : f
    ));
  };

  // Valor total dos fornecedores selecionados
  const valorTotalSelecionado = fornecedores
    .filter(f => f.selecionado)
    .reduce((acc, f) => acc + (Number(f.valorTotal) || 0), 0);

  // Lógica para exibir o botão: se QUANTIDADES < QTD_TOTAL
  const quantidadeFechada = Number(cotacao?.QUANTIDADES) >= Number(cotacao?.QTD_TOTAL);

  // Colunas da tabela
  const columns = [
    {
      title: 'Nome dos fornecedores',
      dataIndex: 'nome',
      key: 'nome',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
    },
    {
      title: 'Valor',
      dataIndex: 'valor',
      key: 'valor',
      render: valor => valor ? `R$ ${Number(valor).toLocaleString('pt-BR', {minimumFractionDigits: 2})}` : '',
    },
    {
      title: 'Qnt',
      dataIndex: 'quantidade',
      key: 'quantidade',
    },
    {
      title: 'Valor total',
      dataIndex: 'valorTotal',
      key: 'valorTotal',
      render: valor => valor ? `R$ ${Number(valor).toLocaleString('pt-BR', {minimumFractionDigits: 2})}` : '',
    },
  ];

  // Formatar data
  function formatarData(dataStr) {
    if (!dataStr) return '';
    const d = new Date(dataStr);
    if (isNaN(d)) return dataStr;
    return d.toLocaleDateString('pt-BR');
  }

  const fornecedoresMock = [
    { id: 1, nome: 'Farol de Neblina', enviado: true },
    { id: 2, nome: 'Auto Peças Silva', enviado: false },
    { id: 3, nome: 'Peças Rápidas', enviado: false },
  ];

  // Função para aprovar compra
  const handleAprovarCompra = async () => {
    if (!cotacao) return;
    setAprovando(true);
    try {
      const usuarioId = localStorage.getItem('idUsuario');
      const itensSelecionados = cotacao.item_cotacao
        .filter(item => item.item && item.item.IT_ID)
        .map(item => ({ ITC_IT_ID: item.item.IT_ID }));
  
      if (!itensSelecionados.length) {
        setAprovando(false);
        return;
      }
  
      const payload = {
        COM_USU_ID: Number(usuarioId),
        COM_COT_ID: cotacao.COT_ID,
        itens: itensSelecionados 
      };
      
      // console.log('Payload sendo enviado:', payload);
      // console.log('Itens selecionados:', itensSelecionados);
      // console.log('ID da cotação:', cotacao.COT_ID);
      // console.log('ID do usuário:', usuarioId);
      
      const response = await api.post(API_CONFIG.ENDPOINTS.COMPRAS.APROVAR_COMPRA, payload);
      // console.log('Resposta da API ao aprovar compra:', response.data);
      
      // Sempre exibir como sucesso se a requisição foi bem-sucedida
      message.success('Compra aprovada com sucesso!');
      navigate(-1);
      
    } catch (e) {
      // console.error('Erro ao aprovar compra:', e);
      // console.error('Detalhes do erro:', e.response?.data);
      message.error('Erro ao aprovar compra. Tente novamente.');
    } finally {
      setAprovando(false);
    }
  };

  // Handler para abrir modal e buscar itens faltantes
  const handleAbrirModalItensFaltantes = async () => {
    if (!cotacao?.COT_SOL_ID) {
      message.error('ID da solicitação não encontrado!');
      return;
    }
    //console.log('Solicitação para buscar itens faltantes, ID:', cotacao.COT_SOL_ID);
    try {
      setLoading(true);
      const response = await api.get(API_CONFIG.ENDPOINTS.SOLICITACOES.ITENS_FALTANTES(cotacao.COT_SOL_ID));
      //console.log('Resposta da API itens faltantes:', response);
      if (response.data.status && Array.isArray(response.data.itens_nao_cotados)) {
        setItensFaltantes(response.data.itens_nao_cotados);
        setModalReenviarVisivel(true);
      } else {
        setItensFaltantes([]);
        message.info('Nenhum item faltante encontrado.');
      }
    } catch (e) {
      // console.error('Erro ao buscar itens faltantes:', e);
      if (e.response && e.response.status === 404) {
        setItensFaltantes([]);
        message.info('Nenhum item faltante encontrado.');
      } else {
        setItensFaltantes([]);
        message.error('Erro ao buscar itens faltantes.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Buscar itens faltantes ao montar a tela
  useEffect(() => {
    if (cotacao?.COT_SOL_ID) {
      const fetchItensFaltantes = async () => {
        try {
          const response = await api.get(API_CONFIG.ENDPOINTS.SOLICITACOES.ITENS_FALTANTES(cotacao.COT_SOL_ID));
          if (response.data.status && Array.isArray(response.data.itens_nao_cotados)) {
            setItensFaltantes(response.data.itens_nao_cotados);
          } else {
            setItensFaltantes([]);
          }
        } catch {
          setItensFaltantes([]);
        }
      };
      fetchItensFaltantes();
    }
  }, [cotacao]);
  // Log para depuração do array de itens faltantes
  //console.log('itensFaltantes:', itensFaltantes);
  return (
    <div className="home-container">
      <div className="modal-wrapper">
        <h1 className="modal-title">Verificar cotação</h1>
        <div className="modal-content">
          {loading ? (
            <div>Carregando...</div>
          ) : cotacao ? (
            <>
              <div className="solicitacao-header-box" style={{display: 'flex', flexDirection: 'column', gap: 8, padding: 24, border: '1px solid #222', borderRadius: 8, marginBottom: 24, background: '#fff'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16}}>
                  <div style={{fontSize: '1.25rem', fontWeight: 700, color: '#001f4d'}}>
                    Nº da cotação: <span style={{color: '#001f4d'}}>{cotacao.COT_ID || '1008'}</span>
                  </div>
                  <div style={{fontWeight: 900, color: '#222', fontSize: '1rem', textAlign: 'right', minWidth: 128, flex: 1, justifyContent: 'flex-end', display: 'flex'}}>
                    Status: <span style={{color: 'green', fontWeight: 600, marginLeft: 8}}>{cotacao.COT_STATUS || 'Em cotação'}</span>
                  </div>
                </div>
                <div style={{margin: '6px 0'}}>
                  <b>Fornecedor:</b> <span style={{fontWeight: 400}}>{cotacao.COT_FOR_ID || '---'}</span>
                </div>
                <div style={{margin: '6px 0'}}>
                  <b>Valor total:</b> <span style={{fontWeight: 400}}>{valorTotalSelecionado ? `R$ ${valorTotalSelecionado.toLocaleString('pt-BR', {minimumFractionDigits: 2})}` : '---'}</span>
                </div>
                <div style={{margin: '6px 0'}}>
                  <b>Data de envio:</b> <span style={{fontWeight: 400}}>{cotacao.COT_DATA_VINCULACAO ? formatarData(cotacao.COT_DATA_VINCULACAO) : '---'}</span>
                </div>
              {/*   <div style={{margin: '6px 0'}}>
                  <b>Status:</b> <span style={{fontWeight: 400}}>{cotacao.COT_STATUS || '---'}</span>
                </div>
               <div style={{margin: '6px 0'}}>
                  <b>Quantidades:</b> <span style={{fontWeight: 400}}>{cotacao.QUANTIDADES || cotacao.QTD_TOTAL || '---'}</span>
                </div>*/}
              </div>

              {isMobile ? (
                <div className="tabela-mobile">
                  {fornecedores.map((f, idx) => (
                    <div className="tabela-mobile-item" key={idx}>
                      <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                        <div className="item-nome">{f.nome}</div>
                      </div>
                      <div className="item-quantidade">Quantidade: {f.quantidade}</div>
                      <div style={{color: '#222'}}>Status: <b>{f.status}</b></div>
                      <div style={{color: '#222'}}>Valor: <b>{`R$ ${Number(f.valor).toLocaleString('pt-BR', {minimumFractionDigits: 2})}`}</b></div>
                      <div style={{color: '#222'}}>Valor total: <b>{`R$ ${Number(f.valorTotal).toLocaleString('pt-BR', {minimumFractionDigits: 2})}`}</b></div>
                    </div>
                  ))}
                  <div style={{textAlign: 'right', fontWeight: 600, paddingRight: 8, marginTop: 12}}>
                    {valorTotalSelecionado ? `R$ ${valorTotalSelecionado.toLocaleString('pt-BR', {minimumFractionDigits: 2})}` : ''}
                  </div>
                </div>
              ) : (
                <Table
                  columns={columns}
                  dataSource={fornecedores}
                  pagination={false}
                  rowKey={(record) => record.nome}
                  style={{ background: 'white', marginBottom: 0, clear: 'both' }}
                  className="tabela-solicitacao"
                  footer={() => (
                    <div style={{textAlign: 'right', fontWeight: 600, paddingRight: 16}}>
                      {valorTotalSelecionado ? `R$ ${valorTotalSelecionado.toLocaleString('pt-BR', {minimumFractionDigits: 2})}` : ''}
                    </div>
                  )}
                />
              )}

              <div style={{display: 'flex', justifyContent: isMobile ? 'center' : 'space-between', marginTop: 32, gap: isMobile ? 12 : 0, flexDirection: isMobile ? 'column' : 'row'}}>
                <Button
                  className="submit-button"
                  type="default"
                  onClick={() => navigate(-1)}
                  style={isMobile ? {width: '100%'} : {}}
                >
                  Voltar
                </Button>
                <div style={{display: 'flex', gap: 16, width: isMobile ? '100%' : 'auto', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'flex-end'}}>
                  {!quantidadeFechada && itensFaltantes && itensFaltantes.some(item => (item.IT_STATUS || '').toUpperCase().trim() === 'ABERTO') && (
                    <Button
                      type="primary"
                      danger
                      style={{
                        background: '#d32f2f',
                        borderColor: '#d32f2f',
                        color: '#fff',
                        fontWeight: 600,
                        
                        minWidth: 180,
                        height: 40,
                        fontSize: 15,
                        boxShadow: '0 2px 8px rgba(211,47,47,0.08)',
                        width: isMobile ? '100%' : 'auto'
                      }}
                      onClick={handleAbrirModalItensFaltantes}
                      className="submit-button"
                    >
                      Enviar Itens Faltantes
                    </Button>
                  )}
                  <Button
                    className="submit-button"
                    type="primary"
                    style={{background: '#001f4d', 
                      borderColor: '#001f4d', 
                      width: isMobile ? '100%' : undefined
                    }}
                    onClick={handleAprovarCompra}
                    loading={aprovando}
                    disabled={aprovando}
                  >
                    Aprovar compra
                  </Button>
                </div>
              </div>

              {/* Modal de reenviar */}
              <ReenviarSolicitacao
                visible={modalReenviarVisivel}
                onClose={() => setModalReenviarVisivel(false)}
                onConfirm={() => {
                  setFornecedoresReenvio(fornecedoresMock);
                  setModalFornecedoresVisivel(true);
                }}
                itens={itensFaltantes}
                idSolicitacao={cotacao?.COT_SOL_ID}
              />

              {/* Modal de seleção de fornecedores (corrigido) */}
              {modalFornecedoresVisivel && (
                <SelecionarFornecedor
                  onClose={() => {
                    setModalFornecedoresVisivel(false);
                    setModalReenviarVisivel(false);
                  }}
                  fornecedores={fornecedoresMock}
                  onConfirm={() => {
                    setModalFornecedoresVisivel(false);
                    setModalReenviarVisivel(false);
                  }}
                />
              )}
            </>
          ) : (
            <div>Não foi possível carregar a cotação.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AprovarCotacao;